import React, { useEffect, useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { BeatLoader } from "react-spinners";
import ModalDialog from "../common/ModalDialog";
import tailwindClassnames from "../common/TailwindClassnames";
import SelectInput from "../common/SelectInput";
import ProjectTaxonPicker from "./ProjectTaxonPicker";
import {
  addProjectMapping,
  resetProjectMapping,
  updateProjectMapping,
} from "../../api/projectsApi";
import {
  AiMappingsRow,
  EnabledModelItem,
  TaxonomyResponseDto,
} from "../common/ObjectTypes";
import {
  ALL_MODELS_FILTER,
  MappingFormState,
  ModelFilter,
  buildMappingCreateRequest,
  buildMappingUpdateRequest,
  mappingFormError,
  resolveAddMappingModelId,
} from "./aiMappingsHelpers";

export type EditMappingMode = "add" | "edit" | "addOverride";

type ModelOption = { value: number; label: string };

type EditMappingModalProps = {
  open: boolean;
  mode: EditMappingMode;
  /** The row being edited/overridden. Unused (and may be null) in "add" mode. */
  row: AiMappingsRow | null;
  projectId: number;
  /** The container's current model filter. A specific model_id prefills and
   *  locks add-mode's implicit model (today's behavior, unchanged); the
   *  "All models" sentinel means add-mode has no implicit model and must ask
   *  via the selector below (AKL-729 §4). Unused in edit/addOverride modes,
   *  which always use the target row's own `ai_model_id` regardless of this
   *  filter — see `resolveAddMappingModelId`. */
  modelFilter: ModelFilter;
  /** Enabled models for this project — options for the add-mode model
   *  selector shown when `modelFilter` is "All models". */
  models: EnabledModelItem[];
  /** Project-wide status per taxon_id ("in"/"excluded"), covering every row
   *  currently in view — drives the taxon picker's richer selected-species
   *  card, annotated search results, and the informational warning shown
   *  when the picked species is excluded via some other row. */
  taxonStatus: Map<number, "in" | "excluded">;
  onClose: () => void;
  /** Called after a successful save/reset so the parent refetches. */
  onSaved: () => void;
};

const emptyForm: MappingFormState = {
  raw_label: "",
  target_kind: "taxon",
  taxon_id: null,
  display_name_override: "",
};

/**
 * The add/edit-mapping dialog: create a custom label, edit or add-override an
 * existing one, or reset a project override back to its model's system
 * default. Self-contained (owns its own form/save/reset state); the parent
 * only controls which row/mode is open and refetches via `onSaved`.
 */
const EditMappingModal: React.FC<EditMappingModalProps> = ({
  open,
  mode,
  row,
  projectId,
  modelFilter,
  models,
  taxonStatus,
  onClose,
  onSaved,
}) => {
  const { t } = useTranslation();
  const titleId = useId();
  const resetTitleId = useId();

  const [form, setForm] = useState<MappingFormState>(emptyForm);
  const [initialTaxonLabel, setInitialTaxonLabel] = useState<string | null>(
    null,
  );
  const [selectedTaxonDetail, setSelectedTaxonDetail] =
    useState<TaxonomyResponseDto | null>(null);
  const [selectedAddModelId, setSelectedAddModelId] = useState<number | null>(
    null,
  );
  const [formError, setFormError] = useState("");
  const [saving, setSaving] = useState(false);
  const [confirmingReset, setConfirmingReset] = useState(false);
  const [resetting, setResetting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setFormError("");
    if (mode === "edit" && row) {
      setForm({
        raw_label: row.raw_label ?? "",
        target_kind: row.target_kind === "excluded" ? "excluded" : "taxon",
        taxon_id: row.taxon_id,
        display_name_override: row.display_name_override ?? "",
      });
      setInitialTaxonLabel(row.resolved_display_name);
    } else if (mode === "addOverride" && row) {
      setForm({
        raw_label: row.raw_label ?? "",
        target_kind: "taxon",
        taxon_id: row.taxon_id,
        display_name_override: "",
      });
      setInitialTaxonLabel(row.resolved_display_name);
    } else {
      setForm(emptyForm);
      setInitialTaxonLabel(null);
    }
    // Add-mode prefills from a specific filter (today's implicit behavior);
    // under "All models" there's no implicit model, so the selector below
    // starts empty and is required before Save is enabled.
    setSelectedAddModelId(
      mode === "add" && modelFilter !== ALL_MODELS_FILTER ? modelFilter : null,
    );
    setSelectedTaxonDetail(null);
    setConfirmingReset(false);
  }, [open, mode, row, modelFilter]);

  if (!open) return null;

  const rawLabelLocked = mode !== "add";
  const canReset = mode === "edit" && !!row?.canReset;
  const scientificName =
    selectedTaxonDetail?.scientific_name ?? row?.scientific_name ?? null;
  const needsModelSelector =
    mode === "add" && modelFilter === ALL_MODELS_FILTER;
  const effectiveModelId = resolveAddMappingModelId(
    mode,
    modelFilter,
    row,
    selectedAddModelId,
  );
  const modelOptions: ModelOption[] = models.map((m) => ({
    value: m.model_id,
    label: m.status !== "active" ? `${m.name} (${t("Inactive")})` : m.name,
  }));
  const selectedModelOption =
    modelOptions.find((o) => o.value === selectedAddModelId) ?? null;

  const handleSave = async () => {
    if (saving) return;
    if (needsModelSelector && effectiveModelId == null) {
      setFormError("Select a model");
      return;
    }
    const error = mappingFormError(form);
    if (error) {
      setFormError(error);
      return;
    }
    setSaving(true);
    try {
      if (mode === "edit" && row?.mapping_id != null) {
        await updateProjectMapping(
          projectId,
          row.mapping_id,
          buildMappingUpdateRequest(form),
        );
        toast.success(t("Mapping updated"));
      } else if (effectiveModelId != null) {
        await addProjectMapping(
          projectId,
          buildMappingCreateRequest(effectiveModelId, form),
        );
        toast.success(t("Mapping added"));
      }
      onSaved();
      onClose();
    } catch (error: any) {
      // 4xx failures (e.g. duplicate label) reject with a normalized
      // {message, status} — network/timeout errors already toast themselves
      // inside the axios interceptor, so only toast here when `status` is set.
      console.error("Error saving mapping:", error);
      if (error?.status) {
        toast.error(error.message);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!row?.mapping_id || resetting) return;
    setResetting(true);
    try {
      await resetProjectMapping(projectId, row.mapping_id);
      toast.success(t("Mapping reset to system default"));
      setConfirmingReset(false);
      onSaved();
      onClose();
    } catch (error: any) {
      console.error("Error resetting mapping:", error);
      if (error?.status) {
        toast.error(error.message);
      }
    } finally {
      setResetting(false);
    }
  };

  const title =
    mode === "edit"
      ? t("Edit mapping")
      : mode === "addOverride"
        ? t("Add project override")
        : t("Add custom mapping");

  return (
    <>
      {!confirmingReset && (
        <ModalDialog
          onClose={onClose}
          busy={saving}
          labelledBy={titleId}
          overlayClassName="z-50"
          panelClassName="max-w-3xl"
        >
          <>
            <div className={tailwindClassnames.modalHeaderRow}>
              <h2 id={titleId} className={tailwindClassnames.modalTitle}>
                {title}
              </h2>
              <button
                type="button"
                className={tailwindClassnames.modalCloseButton}
                onClick={onClose}
                aria-label={t("Close")}
              >
                &times;
              </button>
            </div>
            <div className={`${tailwindClassnames.modalBody} space-y-4`}>
              {needsModelSelector && (
                <div className="flex flex-col gap-1">
                  <SelectInput
                    options={modelOptions}
                    className="w-full rounded shadow text-left text-sm"
                    isDisabled={false}
                    isLoading={false}
                    isClearable={false}
                    isSearchable={true}
                    isMulti={false}
                    name={t("Model")}
                    value={selectedModelOption}
                    onChange={(option: ModelOption | null) => {
                      setSelectedAddModelId(option ? option.value : null);
                      if (option) setFormError("");
                    }}
                    errors={formError === "Select a model" ? t(formError) : ""}
                    searchBar={false}
                  />
                </div>
              )}
              <div className="flex flex-col gap-1">
                <span className={tailwindClassnames.textInputLabel}>
                  {rawLabelLocked ? t("Raw model output") : t("Raw label")}
                </span>
                {rawLabelLocked ? (
                  <span
                    className="self-start font-mono text-sm rounded border border-off-white-dark dark:border-off-black-light bg-primary-off-white dark:bg-storm-blue px-2 py-1 text-primary-off-black dark:text-primary-off-white"
                    data-testid="mapping-raw-label"
                  >
                    {form.raw_label}
                  </span>
                ) : (
                  <input
                    id="mapping-raw-label"
                    type="text"
                    className={tailwindClassnames.detailsPageTextInput}
                    value={form.raw_label}
                    onChange={(e) => {
                      setForm((f) => ({ ...f, raw_label: e.target.value }));
                      if (e.target.value.trim() !== "") setFormError("");
                    }}
                  />
                )}
                {formError === "Raw label is required" && (
                  <p className="bg-primary-orange-light rounded text-primary-orange-dark text-sm px-3 py-2">
                    {t(formError)}
                  </p>
                )}
              </div>

              <ProjectTaxonPicker
                name={t("Taxon")}
                value={form.taxon_id}
                initialLabel={initialTaxonLabel}
                onChange={(id) => {
                  setForm((f) => ({ ...f, taxon_id: id }));
                  if (id != null) setFormError("");
                }}
                taxonStatus={taxonStatus}
                cardLabel={t("Species this label resolves to")}
                searchLabel={
                  rawLabelLocked
                    ? t("Point this label at a different species")
                    : t("Search the taxonomy")
                }
                onSelectedDetailChange={setSelectedTaxonDetail}
              />
              {formError === "Select a taxon" && (
                <p className="bg-primary-orange-light rounded text-primary-orange-dark text-sm px-3 py-2">
                  {t(formError)}
                </p>
              )}

              <div className="flex flex-col gap-1">
                <span className={tailwindClassnames.textInputLabel}>
                  {t("Status")}
                </span>
                <span className="self-start inline-flex border border-off-white-dark dark:border-off-black-light rounded overflow-hidden">
                  <button
                    type="button"
                    className={
                      form.target_kind !== "excluded"
                        ? tailwindClassnames.segmentedControlActive
                        : tailwindClassnames.segmentedControlInactive
                    }
                    onClick={() =>
                      setForm((f) => ({ ...f, target_kind: "taxon" }))
                    }
                  >
                    {t("In species list")}
                  </button>
                  <button
                    type="button"
                    className={
                      form.target_kind === "excluded"
                        ? tailwindClassnames.segmentedControlActive
                        : tailwindClassnames.segmentedControlInactive
                    }
                    onClick={() =>
                      setForm((f) => ({ ...f, target_kind: "excluded" }))
                    }
                  >
                    {t("Excluded")}
                  </button>
                </span>
                <p className={`${tailwindClassnames.helperText} !mt-1`}>
                  {t(
                    "Applies to this raw label across every model that outputs it, project-wide — not to other labels resolving to the same species.",
                  )}
                </p>
              </div>

              {form.target_kind === "taxon" && (
                <>
                  <div className="flex flex-col gap-1">
                    <label
                      htmlFor="mapping-display-name"
                      className={tailwindClassnames.textInputLabel}
                    >
                      {t("Name reviewers see")}{" "}
                      <span className="text-greyed-text font-normal">
                        — {t("optional")}
                      </span>
                    </label>
                    <input
                      id="mapping-display-name"
                      type="text"
                      className={tailwindClassnames.detailsPageTextInput}
                      value={form.display_name_override}
                      onChange={(e) =>
                        setForm((f) => ({
                          ...f,
                          display_name_override: e.target.value,
                        }))
                      }
                    />
                    {scientificName ? (
                      <p className={`${tailwindClassnames.helperText} !mt-1`}>
                        {t(
                          "This name is how {{name}} appears to reviewers everywhere in this project — in the review feed and for any other model that resolves to it.",
                          { name: scientificName },
                        )}
                      </p>
                    ) : (
                      <p className={`${tailwindClassnames.helperText} !mt-1`}>
                        {t(
                          "This name is how this species appears to reviewers everywhere in this project — in the review feed and for any other model that resolves to it.",
                        )}
                      </p>
                    )}
                    <p className={`${tailwindClassnames.helperText} !mt-1`}>
                      {t(
                        "Exports use the canonical scientific name, not this label.",
                      )}
                    </p>
                  </div>
                </>
              )}
            </div>

            <div
              className={`${tailwindClassnames.modalFooter} flex-wrap items-center`}
            >
              {canReset && (
                <button
                  type="button"
                  className="mr-auto text-primary-orange-dark hover:underline text-sm text-left"
                  onClick={() => setConfirmingReset(true)}
                  data-testid="reset-mapping-button"
                >
                  {t("Reset to model default")}
                </button>
              )}
              <button
                type="button"
                className={tailwindClassnames.btnSecondary}
                onClick={onClose}
              >
                {t("Cancel")}
              </button>
              <button
                type="button"
                className={`relative ${tailwindClassnames.btnPrimary}`}
                onClick={handleSave}
                disabled={saving}
                data-testid="save-mapping-button"
              >
                {/* Label stays in layout while saving so the button keeps its
                    width; currentColor spinner reads on the filled button in
                    both themes. */}
                <span className={saving ? "invisible" : ""}>{t("Save")}</span>
                {saving && (
                  <span className="absolute inset-0 flex items-center justify-center">
                    <BeatLoader
                      color="currentColor"
                      loading={true}
                      size={10}
                      margin={5}
                    />
                  </span>
                )}
              </button>
            </div>
          </>
        </ModalDialog>
      )}

      {/* The Reset confirm replaces (never stacks on) the main dialog above,
          so exactly one modalOverlay backdrop is visible at a time. z-[60]
          preserves the AKL-715 stacking order: the confirm layer sits above
          the main dialog's z-50 layer. */}
      {confirmingReset && (
        <ModalDialog
          onClose={() => setConfirmingReset(false)}
          busy={resetting}
          labelledBy={resetTitleId}
          overlayClassName="z-[60]"
          panelClassName="max-w-md"
        >
          <>
            <div className={tailwindClassnames.modalHeaderRow}>
              <div className="flex items-center gap-3">
                <span
                  aria-hidden="true"
                  className={tailwindClassnames.modalDangerIcon}
                >
                  !
                </span>
                <h2 id={resetTitleId} className={tailwindClassnames.modalTitle}>
                  {t("Reset mapping?")}
                </h2>
              </div>
              <button
                type="button"
                className={tailwindClassnames.modalCloseButton}
                onClick={() => setConfirmingReset(false)}
                disabled={resetting}
                aria-label={t("Close")}
              >
                &times;
              </button>
            </div>
            <p className={tailwindClassnames.modalBody}>
              {t(
                "Reset this mapping to the model's default? Your project override will be removed, and if the label is excluded it will be included again — its species will appear on the project species list.",
              )}
            </p>
            <div className={tailwindClassnames.modalFooter}>
              <button
                type="button"
                className={tailwindClassnames.btnSecondary}
                onClick={() => setConfirmingReset(false)}
                disabled={resetting}
              >
                {t("Cancel")}
              </button>
              <button
                type="button"
                className={`relative ${tailwindClassnames.btnDanger}`}
                onClick={handleReset}
                disabled={resetting}
              >
                {/* Label stays in layout while resetting so the button keeps
                    its width; currentColor spinner reads on the orange fill in
                    both themes. */}
                <span className={resetting ? "invisible" : ""}>
                  {t("Reset")}
                </span>
                {resetting && (
                  <span className="absolute inset-0 flex items-center justify-center">
                    <BeatLoader
                      color="currentColor"
                      loading={true}
                      size={10}
                      margin={5}
                    />
                  </span>
                )}
              </button>
            </div>
          </>
        </ModalDialog>
      )}
    </>
  );
};

export default EditMappingModal;
